import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { colors } from "../../components/theme";
import { ThemeProvider } from "@material-ui/core/styles";
import { styled } from "@mui/material/styles";
import HeaderViewer from "./components/HeaderViewer/HeaderViewer";
import { createTheme } from "@material-ui/core/styles";
import FieldsDnDList from "./components/FieldsDnList/FieldsDnList";
import Loading from "../../components/loading";
import { uploadFileForTemplate } from "../../services/FileUploadService";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  getTemplateInfoById,
  updateTemplateFields,
  appendDocumentToTemplate,
} from "../../services/templateV2Service";
import { getCanonicalPath } from "../../services/ocr";
import DocumentEditor from "./documentEditor/documentEditor";
import { FieldModel, Group } from "./documentEditor/types";
import { useFields } from "./hooks/useFields";
import { uuidv4 } from "./utils/uuid";
import {
  FieldTypeBffAnalize,
  parseFields,
  parseFieldsAnalize,
  parseFieldsToBff,
} from "./utils/parseFields";
import ContextMenu from "./components/ContextMenu/ContextMenu";
import NbTreeFile from "../../components/tree/treeFile";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import PublishDialog from "./components/PublishDialog/PublishDialog";

// Define Redux state types
interface RootState {
  fileReducer: {
    uploadResponse: {
      fields: Array<FieldTypeBffAnalize>;
      highlighted_pdf_url: string;
      ai_insights: string;
    };
    selectedFile: File | null;
  };
  Tree: {
    responseTree: any;
    loadingTree: boolean;
  };
}

// Define component props
interface TemplateBuilderProps {}

const theme = createTheme({
  palette: {
    primary: {
      main: colors.blue,
    },
    secondary: {
      main: "#dc004e",
    },
  },
  typography: {
    fontFamily: ["Roboto", "Helvetica", "sans-serif"].join(","),
  },
});

const useStyles = makeStyles((theme) => ({
  templateBuilderContainerPreview: {
    gridTemplateColumns: "minmax(0, 1fr) 420px",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  previewMain: {
    gridColumn: "1 / 2",
    [theme.breakpoints.down("md")]: {
      gridColumn: "1 / 2",
    },
  },
  previewSidebar: {
    gridColumn: "2 / 3",
    [theme.breakpoints.down("md")]: {
      gridColumn: "1 / 2",
    },
  },
  templateBuilderContainer: {
    marginTop: 0,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 20,
    width: "100%",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 12,
    },
  },
  pdfViewerContainer: {
    width: "100%",
    height: "100vh",
    backgroundColor: "#f0f0f0",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      height: "auto",
      minHeight: "60vh",
    },
  },
  fieldsPreviewContainer: {
    width: "100%",
    backgroundColor: "#ffffff",
    paddingTop: 16,
    paddingLeft: 24,
    paddingRight: 24,
    borderRadius: 8,
    maxHeight: "calc(100vh - 220px)",
    overflowY: "auto",
    overflowX: "hidden",
    paddingBottom: 32,
    [theme.breakpoints.down("md")]: {
      width: "100%",
      paddingLeft: 16,
      paddingRight: 16,
      maxHeight: "none",
      paddingBottom: 80,
    },
  },
  fieldsPreviewTitle: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 16,
  },
  divider: {
    borderTop: "2px solid #0076FF1A",
    margin: "16px 0",
  },
}));

const TemplateBuilder: React.FC<TemplateBuilderProps> = () => {
  const [tabValue, setTabValue] = useState<number>(0);
  const classes = useStyles();
  const [currentDocumentUrl, setCurrentDocumentUrl] = useState<string>("");
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isPlacingField, setIsPlacingField] = useState<boolean>(false);
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const { id: currentTemplateId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const [selectedFolderDisplay, setSelectedFolderDisplay] = useState<string>(""); // name or path for UI
  const [templateName, setTemplateName] = useState<string>("Untitled template");
  const [contextMenu, setContextMenu] = useState<{
    show: boolean;
    x: number;
    y: number;
    fieldIndex: number;
    fieldId?: string;
  }>({ show: false, x: 0, y: 0, fieldIndex: -1 });
  // delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [pageToDelete, setPageToDelete] = useState<number | null>(null);
  const [pagesToDelete, setPagesToDelete] = useState<number[]>([]);
  const [numPages, setNumPages] = useState<number | null>(null);
  const { fields, setFields } = useFields({
    currentTemplateId: currentTemplateId || "",
    setLoading: setLoadingPage,
  });
  const [groups, setGroups] = useState<Group[]>([
    { id: 'group-1-unique-id', name: 'Default', color: 'lightblue' },
  ]);
  const [openPublish, setOpenPublish] = useState<boolean>(false);
  const [recipients, setRecipients] = useState<Array<{ name: string; email: string; order: number }>>([]);
  const customerId = localStorage.getItem("customerId") || "";
  const token = (() => {
    const docplaceStr = localStorage.getItem("docplace");
    if (!docplaceStr) return "";
    try {
      const docplaceObj = JSON.parse(docplaceStr);
      return docplaceObj.token || "";
    } catch {
      return "";
    }
  })();
  // Debug context menu state changes
  useEffect(() => {
    console.log("Context menu state changed:", contextMenu);
  }, [contextMenu]);

  const { responseUploadFile } = useSelector(
    (state: RootState) => ({
      responseUploadFile: state.fileReducer.uploadResponse,
    })
  );

  // compute maximum page number from fields/currentPage/numPages
  const maxPage = Math.max(
    1,
    currentPage,
    numPages || 0,
    ...(fields && fields.length
      ? fields.map((f) => f.bounds.page)
      : [currentPage])
  );

  // Escape cancels field placement mode
  useEffect(() => {
    if (!isPlacingField) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsPlacingField(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isPlacingField]);

  useEffect(() => {
    console.log("modes", searchParams.get("mode"));
    if (currentTemplateId && mode === "create") {
      loadTemplateByCreate();
    } else if (currentTemplateId && mode === "edit") {
      getTemplateInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplateId]);

  const onTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const loadTemplateByCreate = async () => {
    console.log("responseUploadFile", responseUploadFile);
    if (responseUploadFile) {
      setFields(parseFieldsAnalize(responseUploadFile.fields));
      setCurrentDocumentUrl(responseUploadFile.highlighted_pdf_url);
    }
  };

  const handleUpdateTemplateFields = async () => {
    const archiveIdToSend = (selectedFolder ?? "").trim();
    if (!archiveIdToSend) {
      toast.error("Default Folder is required. Please select a folder before saving.");
      return;
    }
    try {
      setLoadingPage(true);
      const fieldsWithGroups = parseFieldsToBff(fields);
      const body = {
        fields: fieldsWithGroups,
        groups: groups,
        archiveId: archiveIdToSend,
      };
      console.log("Fields with group names:", fieldsWithGroups.map(f => ({ fieldTitle: f.fieldTitle, fieldGroup: f.fieldGroup })));
      console.log("body", body);
      await updateTemplateFields(currentTemplateId || "", body);
      toast.success("Template saved successfully");
      setTimeout(() => {
        navigate("/templates-v2");
      }, 1000);
    } catch (error) {
      console.error("Error updating template fields:", error);
    } finally {
      setLoadingPage(false);
    }
  };

  const handleClose = () => {
    navigate("/templates-v2");
  };

  const getTemplateInfo = async () => {
    try {
      setLoadingPage(true);
      const response = await getTemplateInfoById(currentTemplateId || "");
      console.log("response", response);
      if (response.status === 200) {
        const data = response.data?.data;
        setFields(parseFields(data.new.form[0].fields));
        setTemplateName(data.doc?.fileName || "Untitled template");

        // Load archiveId (Default Folder) from API so it is shown and persisted on save
        const archiveId = data.new?.doc?.archiveId ?? data.doc?.archiveId ?? "";
        setSelectedFolder(archiveId || "");

        // Resolve folder name/path for display when we have an archiveId
        if (archiveId) {
          getCanonicalPath(archiveId)
            .then((res) => {
              if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
                const sorted = [...res.data].sort(
                  (a: { Level?: number }, b: { Level?: number }) => (a.Level ?? 0) - (b.Level ?? 0)
                );
                const pathParts = sorted
                  .filter((cp: { Type?: string }) => cp.Type === "Folder")
                  .map((cp: { Name?: string }) => cp.Name ?? "")
                  .filter(Boolean);
                setSelectedFolderDisplay(pathParts.join(" / ") || "");
              }
            })
            .catch(() => setSelectedFolderDisplay(""));
        } else {
          setSelectedFolderDisplay("");
        }

        // Load groups if they exist in the response
        if (data.groups) {
          setGroups(data.groups);
        }

        if (data.pdfUrl) {
          console.log("NEW PDF URL:", data.pdfUrl);
          setCurrentDocumentUrl(data.pdfUrl);
        } else {
          console.log("OLD Thumbnail URL:", data.doc?.thumbnail);
          setCurrentDocumentUrl(data.doc?.thumbnail || "");
        }
      }
    } catch (error) {
      console.error("Error uploading template:", error);
    } finally {
      setLoadingPage(false);
    }
  };

  const onAddField = () => {
    // Enter click-to-place mode. Field will be created on next click inside the PDF.
    setIsPlacingField(true);
  };

  const placeField = useMemo(
    () => (payload: { page: number; x: number; y: number }) => {
      const newId = uuidv4();

      // Don't assign to default group - let it be "No group" by default
      const newField = {
        id: newId,
        type: "shortText",
        title: "Field",
        bounds: { page: payload.page, x: payload.x, y: payload.y, width: 1, height: 1 },
        placeholder: "Enter text here",
        fieldGroup: undefined,
        fieldGroupColor: undefined,
      } as FieldModel;

      setFields((prev) => [...prev, newField]);
      setIsPlacingField(false);

      // Briefly highlight the new field
      setHighlightedFieldId(newId);
      window.setTimeout(() => setHighlightedFieldId(null), 1200);
    },
    [setFields]
  );

  // Page actions: append, delete, move

  const [appendOpen, setAppendOpen] = useState<boolean>(false);
  const [appendPosition, setAppendPosition] = useState<"start" | "end">("end");
  const [useAiForAppend, setUseAiForAppend] = useState<boolean>(true);

  const handleAppendPage = (afterPage?: number) => {
    // show selection dialog
    setAppendPosition("end");
    setAppendOpen(true);
  };

  const closeAppendDialog = () => setAppendOpen(false);

  const confirmAppend = async (files: { id: string; label: string }[]) => {
    if (!currentTemplateId) return;
    if (!files || files.length === 0) {
      setAppendOpen(false);
      return;
    }
    try {
      setLoadingPage(true);
      for (const file of files) {
        const body = {
          document_id: file.id,
          use_ai: useAiForAppend,
          append_position: appendPosition,
        };
        const resp = await fetch(
          `${process.env.REACT_APP_HOST_API}/forms/api/v1/pages/${currentTemplateId}/append`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              customerId: `${customerId}`,
            },
            body: JSON.stringify(body),
          }
        );
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(`API error: ${resp.status} ${txt}`);
        }
      }
      toast.success("Document(s) appended successfully");
      await getTemplateInfo();
    } catch (err) {
      console.error("Error appending document", err);
      toast.error("Failed to append document");
    } finally {
      setLoadingPage(false);
      setAppendOpen(false);
    }
  };

  const handleFilesDrop = async (files: File[], useAi: boolean) => {
    if (!currentTemplateId || files.length === 0) return;

    try {
      setLoadingPage(true);

      let successCount = 0;
      let failCount = 0;

      for (const file of files) {
        try {
          // Step 1: Upload file to get document_id
          const uploadResponse = await uploadFileForTemplate(file, (progressEvent: any) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Uploading ${file.name}: ${percentCompleted}%`);
          });

          if (!uploadResponse.data || !uploadResponse.data.documentId) {
            throw new Error(`No documentId returned for ${file.name}`);
          }

          const documentId = uploadResponse.data.documentId;

          // Step 2: Append document to template using service
          await appendDocumentToTemplate(currentTemplateId, documentId, useAi, "end");

          successCount++;
        } catch (err) {
          console.error(`Error processing file ${file.name}:`, err);
          failCount++;
        }
      }

      // Show result message
      if (successCount > 0) {
        const aiText = useAi ? " with AI detection" : "";
        const message = files.length === 1
          ? `"${files[0].name}" appended successfully${aiText}`
          : `${successCount} file(s) appended successfully${aiText}`;
        toast.success(message);

        // Refresh template to show new pages
        await getTemplateInfo();
      }

      if (failCount > 0) {
        toast.error(`${failCount} file(s) failed to append`);
      }

    } catch (err) {
      console.error("Error in drag-drop file append:", err);
      toast.error("Error appending file(s)");
    } finally {
      setLoadingPage(false);
    }
  };

  const handleDeletePage = (page?: number | number[]) => {
    if (Array.isArray(page)) {
      // Multiple pages to delete
      setPagesToDelete(page);
      setPageToDelete(null);
    } else {
      // Single page to delete
      const pageIndex = page !== undefined ? page : currentPage;
      setPageToDelete(pageIndex);
      setPagesToDelete([]);
    }
    setShowDeleteDialog(true);
  };

  const confirmDeletePage = async () => {
    const pagesToRemove =
      pagesToDelete.length > 0
        ? pagesToDelete
        : pageToDelete
        ? [pageToDelete]
        : [];

    if (pagesToRemove.length === 0 || !currentTemplateId) {
      setShowDeleteDialog(false);
      return;
    }

    try {
      setLoadingPage(true);
      // call API to remove pages
      const resp = await fetch(
        `${process.env.REACT_APP_HOST_API}/forms/api/v1/pages/${currentTemplateId}/remove`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            customerId: `${customerId}`,
          },
          body: JSON.stringify({ page_numbers: pagesToRemove }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`API error: ${resp.status} ${txt}`);
      }

      const pageText =
        pagesToRemove.length === 1
          ? `Page ${pagesToRemove[0]}`
          : `${pagesToRemove.length} pages`;
      toast.success(`${pageText} deleted`);

      // reload the page to reflect server-side changes
      await getTemplateInfo();
    } catch (err) {
      console.error("Error removing page", err);
      toast.error("Failed to delete page(s)");
    } finally {
      setShowDeleteDialog(false);
      setPageToDelete(null);
      setPagesToDelete([]);
      setLoadingPage(false);
    }
  };
  const handleMovePage = async (
    pageOrDirection:
      | number
      | "up"
      | "down"
      | {
          action: "first" | "last" | "page";
          page?: number;
          sourcePage?: number;
        },
    sourcePage?: number
  ) => {
    // determine source page
    let source = sourcePage || currentPage;

    // If sourcePage is provided via object parameter (from drag and drop), use it instead
    if (typeof pageOrDirection === "object" && pageOrDirection.sourcePage) {
      source = pageOrDirection.sourcePage;
      console.log(`Using sourcePage from drag and drop: ${source}`);
    }

    // determine target index
    let target: number | null = null;

    if (typeof pageOrDirection === "string") {
      // up/down relative moves
      if (pageOrDirection === "up") target = source - 1;
      if (pageOrDirection === "down") target = source + 1;
    } else if (typeof pageOrDirection === "number") {
      // move to explicit page number
      target = pageOrDirection;
    } else if (typeof pageOrDirection === "object") {
      const { action, page } = pageOrDirection;
      if (action === "first") target = 1;
      else if (action === "last") target = maxPage;
      else if (action === "page") {
        if (!page) return; // require explicit page for 'page' action
        target = page;
      }
    }

    if (!target || target < 1) return;
    // clamp target to [1, maxPage]
    if (target > maxPage) target = maxPage;

    const P = source;
    const Q = target;
    console.log(`Request move page ${P} -> ${Q}`);

    if (P === Q) return; // no-op
    let newOrderArray = Array.from({ length: maxPage }, (_, i) => i + 1);
    if (P < Q) {
      // moving down: shift pages P+1..Q up, put P at Q
      newOrderArray.splice(P - 1, 1); // remove P (at index P-1)
      newOrderArray.splice(Q - 1, 0, P); // insert P at index Q-1
    } else if (P > Q) {
      // moving up: shift pages Q..P-1 down, put P at Q
      newOrderArray.splice(P - 1, 1); // remove P (at index P-1)
      newOrderArray.splice(Q - 1, 0, P); // insert P at index Q-1
    }
    console.log("New order array:", newOrderArray);
    try {
      setLoadingPage(true);
      // call API to remove page
      // Retrieve token from localStorage or your auth provider

      const resp = await fetch(
        `${process.env.REACT_APP_HOST_API}/forms/api/v1/pages/${currentTemplateId}/reorder`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            customerId: `${customerId}`,
          },
          body: JSON.stringify({ new_order: newOrderArray }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`API error: ${resp.status} ${txt}`);
      }
      // update local fields: remove fields on that page and shift pages > pageToDelete

      // adjust current page

      toast.success(`Page ${source} moved to ${target}`);
      // reload the page to reflect server-side changes
      await getTemplateInfo();
      // Optionally reload or reset the page after deletion
      // For example, you can reload the template info to refresh the document and fields
    } catch (err) {
      console.error("Error moving page", err);
      toast.error("Failed to move page");
    } finally {
      //setShowDeleteDialog(false);
      setLoadingPage(false);
    }
  };
  const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
      padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
      padding: theme.spacing(1),
    },

    "& .MuiDialog-paper": {
      width: "762px",
      maxWidth: "762px",
    },
  }));
  const handleDragEnd = (result: {
    source: { index: number };
    destination?: { index: number };
  }) => {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) {
      return;
    }
    const updated = Array.from(fields);
    const [moved] = updated.splice(source.index, 1);
    updated.splice(destination.index, 0, moved);
    setFields(updated);
  };

  const handleRemoveField = (index: number) => {
    const updated = [...fields];
    updated.splice(index, 1);
    setFields(updated);
  };

  const handleDuplicateField = (index: number) => {
    const fieldToDuplicate = fields[index];
    const duplicatedField = {
      ...fieldToDuplicate,
      id: uuidv4(),
      title: `${fieldToDuplicate.title} Copy`,
      bounds: {
        ...fieldToDuplicate.bounds,
        x: fieldToDuplicate.bounds.x + 0.2, // Offset position slightly (0.2 inches)
        y: fieldToDuplicate.bounds.y + 0.2,
      },
    } as FieldModel;

    const updated = [...fields];
    updated.splice(index + 1, 0, duplicatedField);
    setFields(updated);
  };

  const handleDuplicateFieldById = (fieldId: string) => {
    const fieldIndex = fields.findIndex((f) => f.id === fieldId);
    if (fieldIndex !== -1) {
      handleDuplicateField(fieldIndex);
    }
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    fieldIndex: number,
    fieldId?: string
  ) => {
    console.log(
      "Context menu triggered for field index:",
      fieldIndex,
      "fieldId:",
      fieldId
    );
    console.log("Field:", fields[fieldIndex]);
    console.log("Mouse position:", event.clientX, event.clientY);
    event.preventDefault();
    event.stopPropagation();

    setContextMenu({
      show: true,
      x: event.clientX,
      y: event.clientY,
      fieldIndex: fieldIndex,
      fieldId: fieldId,
    });

    console.log("Context menu state set:", {
      show: true,
      x: event.clientX,
      y: event.clientY,
      fieldIndex: fieldIndex,
      fieldId: fieldId,
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, fieldIndex: -1 });
  };

  const handleSaveAndSend = async () => {
    try {
      setLoadingPage(true);
      const fieldsWithGroups = parseFieldsToBff(fields);
      const body = {
        fields: fieldsWithGroups,
        groups: groups,
        archiveId: selectedFolder,
      };
      await updateTemplateFields(currentTemplateId || "", body);
      toast.success("Template saved successfully");
      setOpenPublish(true);
    } catch (error) {
      console.error("Error updating template fields:", error);
      toast.error("Failed to save template");
    } finally {
      setLoadingPage(false);
    }
  };

  const handlePublish = (templateId: string) => {
    setOpenPublish(false);
    navigate(`/templates-v2/template-builder-recipients/${templateId}`);
  };

  return (
    <div>
      <ThemeProvider theme={theme}>
        <ToastContainer />
        <HeaderViewer
          tabValue={tabValue}
          onTabChange={onTabChange}
          onSaveAndClose={handleUpdateTemplateFields}
          onSaveAndSend={handleSaveAndSend}
          onClose={handleClose}
          onAddField={onAddField}
          templateName={templateName}
          showFolderWarning={!((selectedFolder ?? "").trim())}
          onSelectFolder={() => {
            // Ensure inspector is visible then scroll to the folder selector
            setTabValue(0);
            window.setTimeout(() => {
              const el = document.getElementById("default-folder-section");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });

              // Trigger the PathTree "Open" action deterministically
              const openEl = document.getElementById(
                "template-builder-default-folder-open"
              ) as HTMLElement | null;
              openEl?.click();
            }, 50);
          }}
          onAppendPage={handleAppendPage}
          onDeletePage={handleDeletePage}
          onMovePage={(dir: any) => handleMovePage(dir)}
          pageCount={maxPage}
          currentPage={currentPage}
          hasSelectedPage={!!currentPage && currentPage > 0}
        />
        {/* Delete confirmation dialog */}
        {showDeleteDialog && (
          <div>
            <Dialog
              open={showDeleteDialog}
              onClose={() => setShowDeleteDialog(false)}
            >
              <DialogTitle>
                {pagesToDelete.length > 0
                  ? `Delete ${pagesToDelete.length} pages`
                  : "Delete page"}
              </DialogTitle>
              <DialogContent>
                {pagesToDelete.length > 0
                  ? `Are you sure you want to delete ${pagesToDelete.length} selected pages?`
                  : `Are you sure you want to delete page ${pageToDelete}?`}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowDeleteDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={() => confirmDeletePage()} color="secondary">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        )}

        {/* Append document dialog */}
        {appendOpen && (
          <BootstrapDialog
            onClose={closeAppendDialog}
            aria-labelledby="customized-dialog-title"
            open={appendOpen}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={useAiForAppend}
                  onChange={(e) => setUseAiForAppend(e.target.checked)}
                  name="useAiForAppend"
                  color="primary"
                />
              }
              label="Use AI to extract fields"
            />
            <NbTreeFile
              onClickClose={() => closeAppendDialog()}
              onClickRequest={(files) => confirmAppend(files)}
            />
          </BootstrapDialog>
        )}
        <div
          className={`${classes.templateBuilderContainer} ${
            tabValue === 1 ? classes.templateBuilderContainerPreview : ""
          }`}
        >
          <div
            className={`${classes.pdfViewerContainer} ${
              tabValue === 1 ? classes.previewMain : ""
            }`}
            style={{
              width: "100%",
            }}
          >
            {currentDocumentUrl && (
              <DocumentEditor
                fileUrl={currentDocumentUrl}
                templateName={templateName}
                templateId={currentTemplateId}
                fields={fields}
                setFields={setFields}
                showInspector={tabValue === 0}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
                selectedFolderDisplay={selectedFolderDisplay}
                setSelectedFolderDisplay={setSelectedFolderDisplay}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                onContextMenu={handleContextMenu}
                onDuplicateField={handleDuplicateFieldById}
                onAppendPage={handleAppendPage}
                onDeletePage={handleDeletePage}
                onMovePage={(page: any, sourcePage?: number) =>
                  handleMovePage(page, sourcePage)
                }
                onNumPages={(n: number) => setNumPages(n)}
                onFilesDrop={handleFilesDrop}
                groups={groups}
                onGroupsChange={setGroups}
                placingField={isPlacingField}
                onPlaceField={placeField}
                highlightedFieldId={highlightedFieldId}
                onRequestAddField={onAddField}
              />
            )}
          </div>

          {tabValue === 1 && (
            <div className={`${classes.fieldsPreviewContainer} ${classes.previewSidebar}`}>
              <div className={classes.fieldsPreviewTitle}>Fields Preview</div>
              <div className={classes.divider}></div>

              <FieldsDnDList
                fields={fields}
                onFieldsChange={setFields}
                onDragEnd={handleDragEnd}
                onRemoveField={handleRemoveField}
                onContextMenu={handleContextMenu}
              />
            </div>
          )}
        </div>

        {contextMenu.show && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onDuplicate={() => {
              if (contextMenu.fieldId) {
                handleDuplicateFieldById(contextMenu.fieldId);
              } else {
                handleDuplicateField(contextMenu.fieldIndex);
              }
            }}
            onClose={closeContextMenu}
          />
        )}

        {/* Publish dialog */}
        <PublishDialog
          open={openPublish}
          onClose={() => setOpenPublish(false)}
          onPublish={handlePublish}
          recipients={recipients}
          setRecipients={setRecipients}
          templateId={currentTemplateId}
        />

        <Loading show={loadingPage} text={"Processing..."} />
      </ThemeProvider>
    </div>
  );
};

export default TemplateBuilder;
